import { Check, CreditCard, FileText, Wallet } from "lucide-react";
import { useLang } from "../i18n";
import { Eyebrow, Reveal } from "./Reveal";

export default function Pricing() {
  const { t } = useLang();
  return (
    <section id="pricing" className="bg-cream py-24 lg:py-28">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <Reveal>
          <Eyebrow>{t.pricing.eyebrow}</Eyebrow>
          <h2
            data-testid="pricing-heading"
            className="mt-5 font-serif text-4xl text-[#141414] sm:text-5xl"
          >
            {t.pricing.title}
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-[1.7fr_1fr]">
          <Reveal className="h-full">
            <div
              data-testid="pricing-tariff-card"
              className="h-full rounded-2xl border border-black/5 bg-white p-8 shadow-sm"
            >
              <div className="flex items-start justify-between">
                <Eyebrow>{t.pricing.tariff}</Eyebrow>
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-gold">
                  <CreditCard className="h-5 w-5" />
                </span>
              </div>
              <a
                data-testid="pricing-tariff-pdf-link"
                href="https://www.erlangen-hoechstadt.de/media/12854/taxi-tarif-ordnung-2023.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 flex items-center gap-3 rounded-xl border border-gold/40 bg-gold/5 px-5 py-5 text-[15px] font-bold text-gold-dark transition-colors hover:bg-gold/10"
              >
                <FileText className="h-5 w-5 shrink-0 text-gold" />
                {t.pricing.tariffLink}
              </a>
              <p className="mt-4 text-xs text-gray-500">{t.pricing.tariffNote}</p>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="h-full">
            <div
              data-testid="pricing-distance-card"
              className="flex h-full flex-col rounded-2xl bg-[#111] p-8"
            >
              <Eyebrow>{t.pricing.distTitle}</Eyebrow>
              <div className="mt-7 space-y-3">
                {t.pricing.dist.map((line, i) => (
                  <div
                    key={line}
                    data-testid={`pricing-promise-${i + 1}`}
                    className="flex items-center gap-4 rounded-xl bg-[#1c1c1c] px-5 py-4"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gold">
                      <Check className="h-4 w-4 text-black" strokeWidth={3.5} />
                    </span>
                    <span className="text-sm font-semibold text-white/90">
                      {line}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-auto flex items-center gap-2 pt-8 text-sm text-white/60">
                <CreditCard className="h-4 w-4 text-gold" />
                {t.pricing.distNote}
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div
            data-testid="payments-card"
            className="mx-auto mt-16 max-w-3xl rounded-2xl border border-black/5 bg-white p-8 shadow-sm"
          >
            <div className="flex items-start justify-between">
              <div>
                <Eyebrow>{t.payments.eyebrow}</Eyebrow>
                <h3 className="mt-4 font-serif text-3xl text-[#141414]">
                  {t.payments.title}
                </h3>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-gold">
                <Wallet className="h-5 w-5" />
              </span>
            </div>
            <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-5">
              {t.payments.methods.map(([name, sub]) => (
                <div
                  key={name}
                  className="rounded-xl border border-black/10 px-3 py-4 text-center"
                >
                  <div className="text-sm font-extrabold text-[#1a1a1a]">
                    {name}
                  </div>
                  {sub ? (
                    <div className="mt-0.5 text-[11px] text-gray-500">{sub}</div>
                  ) : (
                    <div className="mt-0.5 text-[11px]">&nbsp;</div>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-gray-600">{t.payments.note}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
